// ===========================================
// Save the Dates React Query Hooks (Admin)
// ===========================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  saveTheDateApi,
  SaveTheDate,
  SaveTheDateInput,
  SaveTheDateStatus,
} from '../api/saveTheDates.api';

export const saveTheDateKeys = {
  all: ['save-the-dates'] as const,
  lists: () => [...saveTheDateKeys.all, 'list'] as const,
  list: (filters?: { status?: SaveTheDateStatus; eventId?: string }) =>
    [...saveTheDateKeys.lists(), filters] as const,
  detail: (id: string) => [...saveTheDateKeys.all, 'detail', id] as const,
  channels: () => [...saveTheDateKeys.all, 'channels'] as const,
};

type ApiError = Error & { response?: { data?: { message?: string } } };
const errMsg = (e: unknown, fallback: string) =>
  (e as ApiError)?.response?.data?.message || fallback;

export function useSaveTheDates(filters?: { status?: SaveTheDateStatus; eventId?: string }) {
  return useQuery({
    queryKey: saveTheDateKeys.list(filters),
    queryFn: () => saveTheDateApi.list(filters),
  });
}

export function useSaveTheDate(id: string) {
  return useQuery({
    queryKey: saveTheDateKeys.detail(id),
    queryFn: () => saveTheDateApi.get(id),
    enabled: !!id,
  });
}

export function useAvailableChannels() {
  return useQuery({
    queryKey: saveTheDateKeys.channels(),
    queryFn: () => saveTheDateApi.availableChannels(),
    staleTime: 1000 * 60 * 60, // availability rarely changes
  });
}

export function useCreateSaveTheDate() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (data: SaveTheDateInput) => saveTheDateApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: saveTheDateKeys.lists() });
      enqueueSnackbar('Save the Date created', { variant: 'success' });
    },
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to create'), { variant: 'error' }),
  });
}

export function useUpdateSaveTheDate() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SaveTheDateInput> }) =>
      saveTheDateApi.update(id, data),
    onSuccess: (_: SaveTheDate, { id }) => {
      queryClient.invalidateQueries({ queryKey: saveTheDateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: saveTheDateKeys.detail(id) });
      enqueueSnackbar('Save the Date updated', { variant: 'success' });
    },
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to update'), { variant: 'error' }),
  });
}

export function useDeleteSaveTheDate() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (id: string) => saveTheDateApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: saveTheDateKeys.lists() });
      enqueueSnackbar('Save the Date deleted', { variant: 'success' });
    },
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to delete'), { variant: 'error' }),
  });
}

export function useScheduleSaveTheDate() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ id, scheduledFor }: { id: string; scheduledFor: string }) =>
      saveTheDateApi.schedule(id, scheduledFor),
    onSuccess: (_: SaveTheDate, { id }) => {
      queryClient.invalidateQueries({ queryKey: saveTheDateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: saveTheDateKeys.detail(id) });
      enqueueSnackbar('Save the Date scheduled', { variant: 'success' });
    },
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to schedule'), { variant: 'error' }),
  });
}

export function useSendSaveTheDate() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (id: string) => saveTheDateApi.send(id),
    onSuccess: (res, id) => {
      queryClient.invalidateQueries({ queryKey: saveTheDateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: saveTheDateKeys.detail(id) });
      enqueueSnackbar(res.sent ? 'Save the Date sent' : 'Already sent', {
        variant: res.sent ? 'success' : 'info',
      });
    },
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to send'), { variant: 'error' }),
  });
}

export function useResendSaveTheDate() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    // Plain resend — same content, not framed as amended.
    mutationFn: (id: string) => saveTheDateApi.resend(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: saveTheDateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: saveTheDateKeys.detail(id) });
      enqueueSnackbar('Save the Date resent', { variant: 'success' });
    },
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to resend'), { variant: 'error' }),
  });
}

export function useAmendSaveTheDate() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    // Apply edits, then resend as an Amended Save the Date.
    mutationFn: ({ id, edits }: { id: string; edits?: Partial<SaveTheDateInput> }) =>
      saveTheDateApi.amend(id, edits),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: saveTheDateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: saveTheDateKeys.detail(id) });
      enqueueSnackbar('Amended Save the Date sent', { variant: 'success' });
    },
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to amend'), { variant: 'error' }),
  });
}

export function useConvertSaveTheDateToEvent() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (id: string) => saveTheDateApi.convertToEvent(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: saveTheDateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: saveTheDateKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ['events'] });
      enqueueSnackbar('Event created from Save the Date', { variant: 'success' });
    },
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to convert'), { variant: 'error' }),
  });
}
